import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, IconButton, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Tooltip, Stack, Divider, Alert, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BuildIcon from '@mui/icons-material/Build';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RefreshIcon from '@mui/icons-material/Refresh';
import { maintenanceTicketService, machineService, pressFormService, maintenanceSettingsService, getMaintenanceDashboard } from '../services/api';
import { fmtMachineLabel, sortMachinesByIdentifier } from '../utils/machineLabel';

export const STATUSES = [
  { key: 'new', label: 'Заявка' },
  { key: 'continue_production', label: 'Продовжено' },
  { key: 'service_requested', label: 'Запит обслуговування' },
  { key: 'in_progress', label: 'В роботі' },
  { key: 'done', label: 'Завершено' },
];

export const STATUS_LABEL = Object.fromEntries(STATUSES.map((s) => [s.key, s.label]));

const URGENCY_CONFIG = {
  planned:   { label: 'Планово',   color: 'info' },
  urgent:    { label: 'Терміново', color: 'warning' },
  breakdown: { label: 'Поломка',   color: 'error' },
};

const COLUMN_COLORS = {
  new:               '#e3f2fd',
  continue_production: '#fff3e0',
  service_requested: '#fce4ec',
  in_progress:       '#fff8e1',
  done:              '#e8f5e9',
};
const COLUMN_HEADER_COLORS = {
  new:               '#1976d2',
  continue_production: '#ef6c00',
  service_requested: '#c62828',
  in_progress:       '#f57c00',
  done:              '#388e3c',
};

const KANBAN_COLUMNS = STATUSES.filter((s) => s.key !== 'done').concat(STATUSES.filter((s) => s.key === 'done'));

const AUTHOR_LABELS = { manager: 'Керівник', maintenance: 'Обслуговування', system: 'Система' };
const AUTHOR_COLORS = { manager: 'primary', maintenance: 'secondary', system: 'default' };

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function CommentThread({ comments }) {
  if (!comments || comments.length === 0) return null;
  return (
    <Box sx={{ mt: 1, borderTop: '1px solid #e0e0e0', pt: 0.75 }}>
      {comments.map((c) => (
        <Box key={c.id} sx={{ mb: 0.75 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: 0.5, mb: 0.25 }}>
            <Chip
              label={AUTHOR_LABELS[c.author_type] || c.author_type}
              color={AUTHOR_COLORS[c.author_type] || 'default'}
              size="small"
              sx={{ fontSize: 10, height: 18, flexShrink: 0 }}
            />
            {c.action_label && (
              <Chip label={c.action_label} size="small" variant="outlined" sx={{ fontSize: 10, height: 18, flexShrink: 0, maxWidth: 120, overflow: 'hidden' }} />
            )}
            <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>{formatDate(c.created_at)}</Typography>
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', wordBreak: 'break-word', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
            {c.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

// Всі дії за статусом — без прив'язки до ролі на фронті
const ACTIONS_BY_STATUS = {
  // Нова заявка: керівник вирішує що робити
  new: {
    manager: [
      { key: 'service_requested', label: 'Призначити обслуговування', color: 'error',   needsComment: false, withSchedule: true  },
      { key: 'continue_production', label: 'Продовжити виробництво',  color: 'warning', needsComment: true,  withSchedule: false },
    ],
    maintenance: [
      { key: 'in_progress', label: 'Взяти в роботу одразу', color: 'primary', needsComment: false },
    ],
  },
  // Керівник вирішив продовжити виробництво — обслуговування підтверджує або все одно обслуговує
  continue_production: {
    manager: [],
    maintenance: [
      { key: 'in_progress',            label: 'Провести обслуговування',   color: 'primary', needsComment: false },
      { key: 'continue_production_mt', label: 'Підтвердити продовження',   color: 'warning', needsComment: true  },
    ],
  },
  // Керівник призначив обслуговування — обслуговування бере в роботу
  service_requested: {
    manager: [],
    maintenance: [
      { key: 'in_progress', label: 'Взяти в роботу', color: 'primary', needsComment: false },
    ],
  },
  // В роботі — обслуговування завершує
  in_progress: {
    manager: [],
    maintenance: [
      { key: 'done', label: 'Завершити обслуговування', color: 'success', needsComment: false },
    ],
  },
};

export function TicketCard({ ticket, onAction, onEdit, onDelete }) {
  const urg = URGENCY_CONFIG[ticket.urgency] || URGENCY_CONFIG.planned;
  const statusActions = ACTIONS_BY_STATUS[ticket.status] || { manager: [], maintenance: [] };
  const managerActions = statusActions.manager || [];
  const maintenanceActions = statusActions.maintenance || [];

  return (
    <Paper
      elevation={2}
      sx={{
        p: 1.5,
        mb: 1.5,
        borderLeft: '4px solid',
        borderLeftColor:
          ticket.urgency === 'breakdown' ? 'error.main'
          : ticket.urgency === 'urgent' ? 'warning.main'
          : 'info.main',
        '&:hover': { boxShadow: 4 },
        overflow: 'hidden',
        minWidth: 0,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5} sx={{ minWidth: 0 }}>
        <Chip label={urg.label} color={urg.color} size="small" sx={{ flexShrink: 0 }} />
        <Stack direction="row" spacing={0} sx={{ flexShrink: 0 }}>
          {onEdit && (
            <Tooltip title="Редагувати">
              <IconButton size="small" onClick={() => onEdit(ticket)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Видалити">
              <IconButton size="small" color="error" onClick={() => onDelete(ticket.id)}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {(ticket.machine_name || ticket.machine_identifier) && (
        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          ТПА: {fmtMachineLabel({ name: ticket.machine_name, identifier: ticket.machine_identifier })}
        </Typography>
      )}
      {ticket.press_form_identifier && (
        <Typography variant="body2" fontWeight={600} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {ticket.press_form_identifier}{ticket.press_form_name ? ` — ${ticket.press_form_name}` : ''}
        </Typography>
      )}
      {ticket.production_order_number && (
        <Typography variant="caption" color="text.secondary">
          Зам.: {ticket.production_order_number}
        </Typography>
      )}

      {/* Cycles progress */}
      {ticket.cycles_done != null && ticket.cycles_limit != null && (
        <Box sx={{ mt: 0.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="caption" color="text.secondary">Цикли</Typography>
            <Typography variant="caption" fontWeight={700}>{ticket.cycles_done} / {ticket.cycles_limit}</Typography>
          </Box>
          <Box sx={{ height: 6, bgcolor: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, Math.round(ticket.cycles_done / ticket.cycles_limit * 100))}%`, bgcolor: ticket.cycles_done >= ticket.cycles_limit ? '#d32f2f' : '#ed6c02', borderRadius: 3 }} />
          </Box>
          {ticket.minutes_to_maintenance != null && (
            <Typography variant="caption" sx={{ color: ticket.minutes_to_maintenance <= 0 ? 'error.main' : 'text.secondary' }}>
              {ticket.minutes_to_maintenance <= 0
                ? `Прострочено на ${Math.abs(Math.round(ticket.minutes_to_maintenance))} хв`
                : `До обслуговування: ≈${Math.round(ticket.minutes_to_maintenance)} хв`}
            </Typography>
          )}
        </Box>
      )}

      {/* Scheduled maintenance time */}
      {ticket.scheduled_at && (
        <Box sx={{ mt: 0.5, px: 0.75, py: 0.25, bgcolor: '#f3e5f5', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 700 }}>
            🔧 Обслуговування: {new Date(ticket.scheduled_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
        {ticket.reason}
      </Typography>

      <CommentThread comments={ticket.comments} />

      <Divider sx={{ my: 0.75 }} />

      <Typography variant="caption" color="text.disabled">{formatDate(ticket.created_at)}</Typography>

      {/* Дії керівника */}
      {managerActions.length > 0 && (
        <Box sx={{ mt: 0.75, p: 0.75, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
          <Typography variant="caption" fontWeight={700} color="#1565c0" display="block" mb={0.5}>
            👔 Керівник
          </Typography>
          <Stack direction="column" spacing={0.5}>
            {managerActions.map((act) => (
              <Button
                key={act.key}
                size="small"
                variant="contained"
                color={act.color}
                fullWidth
                startIcon={act.needsComment || act.withSchedule ? <ChatBubbleOutlineIcon /> : undefined}
                onClick={() => onAction(ticket, act)}
                sx={{ fontSize: 11, textTransform: 'none', fontWeight: 700 }}
              >
                {act.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {/* Дії обслуговування */}
      {maintenanceActions.length > 0 && (
        <Box sx={{ mt: 0.75, p: 0.75, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffcc80' }}>
          <Typography variant="caption" fontWeight={700} color="#e65100" display="block" mb={0.5}>
            🔧 Обслуговування
          </Typography>
          <Stack direction="column" spacing={0.5}>
            {maintenanceActions.map((act) => (
              <Button
                key={act.key}
                size="small"
                variant="contained"
                color={act.color}
                fullWidth
                startIcon={act.needsComment ? <ChatBubbleOutlineIcon /> : undefined}
                onClick={() => onAction(ticket, act)}
                sx={{ fontSize: 11, textTransform: 'none', fontWeight: 700 }}
              >
                {act.label}
              </Button>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
}

const DASH_STATUS_COLOR = {
  overdue: '#d32f2f',
  warning: '#ed6c02',
  ok: '#2e7d32',
  no_limit: '#9e9e9e',
};
const DASH_STATUS_LABEL = {
  overdue: 'Прострочено',
  warning: 'Попередження',
  ok: 'В нормі',
  no_limit: 'Без ліміту',
};

function MaintenanceDashboard({ data, loading }) {
  if (loading) return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;
  if (!data || data.length === 0) return (
    <Box textAlign="center" pt={8}><Typography color="text.secondary">Немає активних виробництв</Typography></Box>
  );
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
      {data.map((item) => {
        const color = DASH_STATUS_COLOR[item.status_flag] || '#9e9e9e';
        const pct = item.cycles_before_maintenance > 0
          ? Math.min(100, Math.round((item.cycles_done / item.cycles_before_maintenance) * 100))
          : null;
        const minsLeft = item.minutes_to_maintenance;
        return (
          <Box key={item.machine_id} sx={{ border: `2px solid ${color}`, borderRadius: 2, p: 2, bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontWeight={700} fontSize={15} noWrap>{item.machine_name}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{item.machine_identifier}</Typography>
              </Box>
              <Chip
                label={DASH_STATUS_LABEL[item.status_flag] || item.status_flag}
                size="small"
                sx={{ bgcolor: color, color: '#fff', fontWeight: 700, ml: 1, flexShrink: 0 }}
              />
            </Box>
            {item.press_form_name ? (
              <>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.5 }}>
                  {item.press_form_name}
                </Typography>
                {pct !== null && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                      <Typography variant="caption" color="text.secondary">Цикли</Typography>
                      <Typography variant="caption" fontWeight={600}>{item.cycles_done} / {item.cycles_before_maintenance}</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: '#f0f0f0', borderRadius: 4, overflow: 'hidden', mb: 1 }}>
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 4, transition: 'width 0.3s' }} />
                    </Box>
                  </>
                )}
                {minsLeft !== null && (
                  <Typography variant="body2" sx={{ color, fontWeight: 700 }}>
                    {minsLeft <= 0
                      ? `Прострочено на ${Math.abs(minsLeft)} хв`
                      : `До обслуговування: ${minsLeft} хв`}
                  </Typography>
                )}
                {item.scheduled_at && (
                  <Box sx={{ mt: 0.75, px: 0.75, py: 0.25, bgcolor: '#f3e5f5', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 700 }}>
                      🔧 {new Date(item.scheduled_at).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>
                )}
                {item.active_ticket_status && (
                  <Chip
                    label={STATUS_LABEL[item.active_ticket_status] || item.active_ticket_status}
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.75 }}
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">Без прес-форми</Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function emptyForm() {
  return { machine: '', press_form: '', reason: '', urgency: 'planned', notes: '' };
}

function MaintenanceKanbanPage() {
  const [view, setView] = useState('kanban'); // 'kanban' | 'dashboard'

  // ── kanban state ─────────────────────────────────────
  const [tickets, setTickets] = useState([]);
  const [machines, setMachines] = useState([]);
  const [pressForms, setPressForms] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── dashboard state ──────────────────────────────────
  const [dashData, setDashData] = useState([]);
  const [dashLoading, setDashLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [commentDialog, setCommentDialog] = useState(null); // { ticket, act, withSchedule? }
  const [commentText, setCommentText] = useState('');
  const [commentScheduledAt, setCommentScheduledAt] = useState('');
  const [commentSaving, setCommentSaving] = useState(false);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsValue, setSettingsValue] = useState(30);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [ticketsRes, machinesRes, pfRes] = await Promise.all([
        maintenanceTicketService.getAll(),
        machineService.getAll(),
        pressFormService.getAll(),
      ]);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : ticketsRes.data?.results || []);
      const mArr = machinesRes.data?.results || machinesRes.data || [];
      const pfArr = pfRes.data?.results || pfRes.data || [];
      setMachines(sortMachinesByIdentifier(Array.isArray(mArr) ? mArr : []));
      setPressForms(Array.isArray(pfArr) ? pfArr : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const r = await getMaintenanceDashboard();
      setDashData(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error(e); }
    finally { setDashLoading(false); }
  }, []);

  useEffect(() => {
    if (view === 'dashboard') loadDashboard();
  }, [view, loadDashboard]);

  const openSettings = async () => {
    try {
      const r = await maintenanceSettingsService.get();
      setSettingsValue(r.data?.notify_minutes_before ?? 30);
    } catch {}
    setSettingsOpen(true);
  };
  const saveSettings = async () => {
    setSettingsSaving(true);
    try {
      await maintenanceSettingsService.patch({ notify_minutes_before: Number(settingsValue) });
      setSettingsOpen(false);
    } catch (e) { console.error(e); }
    finally { setSettingsSaving(false); }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (ticket) => {
    setEditingId(ticket.id);
    setForm({ machine: ticket.machine || '', press_form: ticket.press_form || '', reason: ticket.reason || '', urgency: ticket.urgency || 'planned', notes: ticket.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.reason.trim()) return;
    setSaving(true);
    try {
      const payload = { machine: form.machine || null, press_form: form.press_form || null, reason: form.reason.trim(), urgency: form.urgency, notes: form.notes.trim() };
      if (editingId) { await maintenanceTicketService.patch(editingId, payload); }
      else { await maintenanceTicketService.create(payload); }
      setDialogOpen(false);
      await loadAll();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleAction = (ticket, act) => {
    if (act.needsComment || act.withSchedule) {
      setCommentText('');
      setCommentScheduledAt('');
      setCommentDialog({ ticket, act });
    } else {
      applyAction(ticket, act, '', '');
    }
  };

  const applyAction = async (ticket, act, comment, scheduledAt) => {
    const newStatus = act.key === 'continue_production_mt' ? 'continue_production' : act.key;
    try {
      const patch = { status: newStatus };
      if (scheduledAt) patch.scheduled_at = scheduledAt;
      await maintenanceTicketService.patch(ticket.id, patch);
      if (comment.trim()) {
        await maintenanceTicketService.addComment(ticket.id, {
          text: comment.trim(),
          author_type: 'maintenance',
          action_label: act.label,
        });
      }
      await loadAll();
    } catch (e) { console.error(e); }
  };

  const handleCommentSubmit = async () => {
    const { act } = commentDialog;
    if (act.needsComment && !commentText.trim()) return;
    if (act.withSchedule && !commentScheduledAt) return;
    setCommentSaving(true);
    try {
      await applyAction(commentDialog.ticket, act, commentText, commentScheduledAt);
      setCommentDialog(null);
    } finally { setCommentSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити цю заявку?')) return;
    try { await maintenanceTicketService.delete(id); setTickets((prev) => prev.filter((t) => t.id !== id)); }
    catch (e) { console.error(e); }
  };

  const byStatus = (status) => tickets.filter((t) => t.status === status);

  if (loading && view === 'kanban') return <Box display="flex" justifyContent="center" pt={8}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BuildIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Обслуговування</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_, v) => { if (v) setView(v); }}
          >
            <ToggleButton value="kanban" sx={{ px: 1.5, gap: 0.5, textTransform: 'none', fontWeight: 700 }}>
              <ViewKanbanIcon sx={{ fontSize: 16 }} /> Канбан
            </ToggleButton>
            <ToggleButton value="dashboard" sx={{ px: 1.5, gap: 0.5, textTransform: 'none', fontWeight: 700 }}>
              <DashboardIcon sx={{ fontSize: 16 }} /> Дашборд
            </ToggleButton>
          </ToggleButtonGroup>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={openSettings} size="small">
            Налаштування
          </Button>
          {view === 'dashboard' && (
            <IconButton size="small" onClick={loadDashboard} disabled={dashLoading}>
              {dashLoading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* ── DASHBOARD VIEW ─────────────────────────────── */}
      {view === 'dashboard' && <MaintenanceDashboard data={dashData} loading={dashLoading} />}

      {/* ── KANBAN VIEW ────────────────────────────────── */}
      {view === 'kanban' && <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(180px, 1fr))', gap: 1.5, alignItems: 'start', overflowX: 'auto', minWidth: 0 }}>
        {KANBAN_COLUMNS.map((col) => {
          const colTickets = byStatus(col.key);
          return (
            <Box key={col.key} sx={{ minWidth: 200 }}>
              <Box sx={{ bgcolor: COLUMN_HEADER_COLORS[col.key], color: '#fff', borderRadius: '8px 8px 0 0', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={700} fontSize={13}>{col.label}</Typography>
                <Chip label={colTickets.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700 }} />
              </Box>
              <Box sx={{ bgcolor: COLUMN_COLORS[col.key], borderRadius: '0 0 8px 8px', p: 1.25, minHeight: 180 }}>
                {colTickets.length === 0 && <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 3 }}>Немає заявок</Typography>}
                {colTickets.map((ticket) => (
                  <TicketCard
                    key={ticket.id}
                    ticket={ticket}
                    onAction={handleAction}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>}

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Редагувати заявку' : 'Нова заявка на обслуговування'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Терміновість</InputLabel>
              <Select value={form.urgency} label="Терміновість" onChange={(e) => setForm((f) => ({ ...f, urgency: e.target.value }))}>
                <MenuItem value="planned">Планово</MenuItem>
                <MenuItem value="urgent">Терміново</MenuItem>
                <MenuItem value="breakdown">Поломка</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>ТПА</InputLabel>
              <Select value={form.machine} label="ТПА" onChange={(e) => setForm((f) => ({ ...f, machine: e.target.value }))}>
                <MenuItem value="">— не вказано —</MenuItem>
                {machines.map((m) => <MenuItem key={m.id} value={m.id}>{fmtMachineLabel(m)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Прес-форма</InputLabel>
              <Select value={form.press_form} label="Прес-форма" onChange={(e) => setForm((f) => ({ ...f, press_form: e.target.value }))}>
                <MenuItem value="">— не вказано —</MenuItem>
                {pressForms.map((pf) => <MenuItem key={pf.id} value={pf.id}>{pf.identifier}{pf.name ? ` — ${pf.name}` : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Причина / опис проблеми" multiline rows={3} fullWidth value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} required />
            <TextField label="Нотатки" multiline rows={2} fullWidth value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.reason.trim()}>{saving ? 'Збереження…' : 'Зберегти'}</Button>
        </DialogActions>
      </Dialog>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Налаштування обслуговування</DialogTitle>
        <DialogContent>
          <TextField
            label="За скільки хвилин надсилати сповіщення"
            type="number"
            fullWidth
            sx={{ mt: 1 }}
            value={settingsValue}
            onChange={(e) => setSettingsValue(e.target.value)}
            inputProps={{ min: 1, max: 1440 }}
            helperText="Коли до планового обслуговування залишиться менше цього часу — надходить сповіщення."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} disabled={settingsSaving}>Скасувати</Button>
          <Button variant="contained" onClick={saveSettings} disabled={settingsSaving}>
            {settingsSaving ? 'Збереження…' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Comment / schedule dialog */}
      <Dialog open={Boolean(commentDialog)} onClose={() => setCommentDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{commentDialog?.act?.label}</DialogTitle>
        <DialogContent>
          {commentDialog?.act?.withSchedule && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Вкажіть час проведення обслуговування — оператор побачить його на плитці.
              </Alert>
              <TextField
                label="Час обслуговування (обов'язково)"
                type="datetime-local"
                fullWidth
                sx={{ mb: 2 }}
                value={commentScheduledAt}
                onChange={(e) => setCommentScheduledAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
          {commentDialog?.act?.needsComment && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Необхідно залишити коментар для відділу обслуговування.
              </Alert>
              <TextField
                label="Коментар (обов'язково)"
                multiline
                rows={4}
                fullWidth
                autoFocus={!commentDialog?.act?.withSchedule}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialog(null)} disabled={commentSaving}>Скасувати</Button>
          <Button
            variant="contained"
            color={commentDialog?.act?.color || 'primary'}
            onClick={handleCommentSubmit}
            disabled={
              commentSaving ||
              (commentDialog?.act?.needsComment && !commentText.trim()) ||
              (commentDialog?.act?.withSchedule && !commentScheduledAt)
            }
          >
            {commentSaving ? 'Збереження…' : 'Підтвердити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MaintenanceKanbanPage;
